import { Controller } from "@hotwired/stimulus"

// Selection mode for the photo grid. Real checkboxes drive per-photo :checked styling via CSS.
// Selection-mode visibility (overlays, hiding video duration) is also CSS-driven via a
// data-selection-active attribute on this controller's element (a Tailwind named group).
export default class extends Controller {
  static targets = ["checkbox", "selectButton", "selectionControls", "count",
                    "variant", "confirmLabel"]
  static outlets = ["snackbar", "modal"]
  static values = {
    photoUrlTemplate: String,
    folderPhotographsUrlTemplate: String,
    downloadUrl: String
  }

  enterSelectionMode() {
    this.element.dataset.selectionActive = ""
    this.selectButtonTarget.classList.add("hidden")
    this.selectionControlsTarget.classList.remove("hidden")
    this.selectionControlsTarget.classList.add("flex")
  }

  exitSelectionMode() {
    delete this.element.dataset.selectionActive
    this.checkboxTargets.forEach(cb => (cb.checked = false))
    this.selectButtonTarget.classList.remove("hidden")
    this.selectionControlsTarget.classList.add("hidden")
    this.selectionControlsTarget.classList.remove("flex")
    this.updateCount()
  }

  handlePhotoClick(event) {
    if (!("selectionActive" in this.element.dataset)) return
    event.stopImmediatePropagation()
    event.preventDefault()

    const checkbox = this.#checkboxFor(event.currentTarget)
    if (!checkbox) return
    checkbox.checked = !checkbox.checked
    this.updateCount()
  }

  selectAll() {
    const allChecked = this.checkboxTargets.every(cb => cb.checked)
    this.checkboxTargets.forEach(cb => (cb.checked = !allChecked))
    this.updateCount()
  }

  updateCount() {
    const count = this.#selectedIds().length
    this.countTarget.textContent = count
    count > 0 ? this.snackbarOutlet.show() : this.snackbarOutlet.hide()
  }

  downloadPhotos() {
    const photoIds = this.#selectedIds()
    if (photoIds.length === 0) return

    // Native form submit so the browser follows the S3 redirect and triggers
    // a file download — fetch/Turbo would try to render the ZIP as HTML.
    const form = document.createElement("form")
    form.method = "post"
    form.action = this.downloadUrlValue
    form.setAttribute("data-turbo", "false")

    const csrf = document.createElement("input")
    csrf.type = "hidden"
    csrf.name = "authenticity_token"
    csrf.value = document.querySelector("meta[name=csrf-token]")?.content || ""
    form.appendChild(csrf)

    photoIds.forEach(id => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "photo_ids[]"
      input.value = id
      form.appendChild(input)
    })

    document.body.appendChild(form)
    form.submit()
    document.body.removeChild(form)
  }

  openFolderPicker() {
    this.#resetFolderCheckboxes()
    this.#modalById("folder-picker-modal")?.open()
  }

  async confirmFolderAssignment() {
    const selectedCheckboxes = [...this.element.querySelectorAll("input[name='folder_ids[]']:checked")]
    if (selectedCheckboxes.length === 0) return

    const photoIds = this.#selectedIds()
    if (photoIds.length === 0) return

    for (const checkbox of selectedCheckboxes) {
      const ok = await this.#sendRequest(this.#folderPhotographsUrl(checkbox.value), "POST", this.#photoIdsForm(photoIds))
      if (!ok) return
    }

    this.#modalById("folder-picker-modal")?.close()
    this.exitSelectionMode()
  }

  // Snackbar: acts on the checked photos. Inside an album it unlinks, everywhere
  // else it deletes for good.
  openDeletionConfirm() {
    const photoIds = this.#selectedIds()
    if (photoIds.length === 0) return

    const folderId = this.#currentFolderId()
    this.#openDeletionConfirm({
      photoIds,
      folderId: folderId,
      variant: folderId ? "selection-folder" : "selection-all"
    })
  }

  // Single photo menu: take it out of the album it is shown in.
  openPhotoFolderRemoval({ currentTarget }) {
    this.#openDeletionConfirm({
      photoIds: [currentTarget.dataset.photoId],
      folderId: currentTarget.dataset.folderId,
      variant: "photo-folder"
    })
  }

  // Single photo menu: delete it everywhere, albums included.
  openPhotoDeletion({ currentTarget }) {
    this.#openDeletionConfirm({
      photoIds: [currentTarget.dataset.photoId],
      folderId: null,
      variant: "photo-all"
    })
  }

  cancelDeletion() {
    this.#modalById("deletion-confirm-modal")?.close()
  }

  async confirmDeletion() {
    if (!this.pendingDeletion) return
    const { photoIds, folderId } = this.pendingDeletion

    const ok = folderId
      ? await this.#removeFromFolder(folderId, photoIds)
      : await this.#deletePhotos(photoIds)

    if (!ok) return

    this.pendingDeletion = null
    this.#modalById("deletion-confirm-modal")?.close()
    // Closes the single photo view when it is showing what just went away.
    this.dispatch("deleted", { target: document })
    this.exitSelectionMode()
  }

  #openDeletionConfirm({ photoIds, folderId, variant }) {
    this.pendingDeletion = { photoIds, folderId }
    this.variantTargets.forEach(el =>
      el.classList.toggle("hidden", el.dataset.variant !== variant)
    )
    this.confirmLabelTarget.textContent = folderId ? "Entfernen" : "Löschen"
    this.#modalById("deletion-confirm-modal")?.open()
  }

  async #removeFromFolder(folderId, photoIds) {
    return this.#sendRequest(this.#folderPhotographsUrl(folderId), "DELETE", this.#photoIdsForm(photoIds))
  }

  // Sequential deletes — stops on first failure so the user can see which photos actually deleted
  // (deleted ones are removed from the feed via per-photo turbo stream response).
  async #deletePhotos(photoIds) {
    for (const id of photoIds) {
      const ok = await this.#sendRequest(this.#photoUrl(id), "DELETE")
      if (!ok) return false
    }
    return true
  }

  async #sendRequest(url, method, body) {
    const csrfToken = document.querySelector("meta[name=csrf-token]")?.content
    try {
      const response = await fetch(url, {
        method,
        headers: {
          "X-CSRF-Token": csrfToken,
          "Accept": "text/vnd.turbo-stream.html, text/html"
        },
        body
      })
      if (!response.ok) return false

      const contentType = response.headers.get("Content-Type") || ""
      if (contentType.includes("turbo-stream")) {
        Turbo.renderStreamMessage(await response.text())
      }
      return true
    } catch {
      return false
    }
  }

  #checkboxFor(buttonEl) {
    const photoEl = buttonEl.closest("[data-photo-selection-target='photo']")
    return photoEl?.querySelector("input[type=checkbox]")
  }

  #selectedIds() {
    return this.checkboxTargets.filter(cb => cb.checked).map(cb => cb.dataset.photoId)
  }

  #resetFolderCheckboxes() {
    this.element.querySelectorAll("input[name='folder_ids[]']").forEach(cb => (cb.checked = false))
  }

  #modalById(id) {
    return this.modalOutlets.find(m => m.element.id === id)
  }

  #currentFolderId() {
    return new URL(window.location.href).searchParams.get("folder_id")
  }

  #photoIdsForm(photoIds) {
    const formData = new FormData()
    photoIds.forEach(id => formData.append("photo_ids[]", id))
    return formData
  }

  #folderPhotographsUrl(folderId) {
    return this.folderPhotographsUrlTemplateValue.replace("FOLDER_ID", folderId)
  }

  #photoUrl(photoId) {
    return this.photoUrlTemplateValue.replace("PHOTO_ID", photoId)
  }
}
